import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MdAddShoppingCart, MdBarChart } from 'react-icons/md'
import {
    getVideoCredits,
    getVideoUsage,
    type VideoCreditBalance,
    type VideoParticipantKind,
    type VideoUsageRow,
} from '@/lib/api/video/video-api'

const KIND_LABELS: Record<VideoParticipantKind, string> = {
    PUBLISHER: 'Participantes',
    VIEWER: 'Espectadores',
    RECORDER: 'Gravação',
}

const KINDS: VideoParticipantKind[] = ['PUBLISHER', 'VIEWER', 'RECORDER']

function isoDay(date: Date): string {
    return date.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })
}

function formatDay(day: string): string {
    const [year, month, dayOfMonth] = day.split('-')
    return `${dayOfMonth}/${month}/${year}`
}

const number = new Intl.NumberFormat('pt-BR')

export default function VideoUsagePage() {
    const today = new Date()
    const [from, setFrom] = useState(isoDay(new Date(today.getFullYear(), today.getMonth(), 1)))
    const [to, setTo] = useState(isoDay(today))
    const [rows, setRows] = useState<VideoUsageRow[]>([])
    const [loading, setLoading] = useState(true)
    const [balance, setBalance] = useState<VideoCreditBalance | null>(null)

    useEffect(() => {
        void getVideoCredits().then(response => {
            if (response.success && response.result) setBalance(response.result)
        })
    }, [])

    const load = useCallback(async () => {
        if (!from || !to || from > to) return
        setLoading(true)
        const response = await getVideoUsage(from, to)
        setRows(response.success ? (response.result ?? []) : [])
        setLoading(false)
    }, [from, to])

    useEffect(() => {
        void load()
    }, [load])

    const totals = useMemo(() => {
        const result: Record<VideoParticipantKind, number> = { PUBLISHER: 0, VIEWER: 0, RECORDER: 0 }
        for (const row of rows) result[row.kind] += row.minutes
        return result
    }, [rows])

    const creditsByDay = useMemo(() => {
        const map = new Map<string, number>()
        for (const row of rows) map.set(row.day, (map.get(row.day) ?? 0) + row.credits)
        return map
    }, [rows])

    const byDay = useMemo(() => {
        const map = new Map<string, Partial<Record<VideoParticipantKind, VideoUsageRow>>>()
        for (const row of rows) {
            const entry = map.get(row.day) ?? {}
            entry[row.kind] = row
            map.set(row.day, entry)
        }
        return [...map.entries()].sort(([a], [b]) => b.localeCompare(a))
    }, [rows])

    return (
        <div className="p-4 sm:p-6 space-y-5">
            <div>
                <h1 className="text-2xl font-semibold">Consumo de vídeo</h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                    Minutos por tipo de participante, somados pelo dia em que cada um saiu da sala
                </p>
            </div>

            {balance?.enabled && (
                <section className="card bg-base-100 border border-base-200">
                    <div className="card-body p-5 gap-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h2 className="font-semibold">Créditos de vídeo</h2>
                                <p className="text-sm text-base-content/60">
                                    1 crédito = 1 minuto por participante com vídeo. Quem só assiste usa{' '}
                                    {balance.rates.viewerMinuteCredits.toLocaleString('pt-BR')} por minuto.
                                </p>
                            </div>
                            <Link to="/system-store?product=video-credits" className="btn btn-primary btn-sm">
                                <MdAddShoppingCart className="text-lg" />
                                Comprar créditos
                            </Link>
                        </div>
                        <div className="stats stats-vertical sm:stats-horizontal border border-base-200 w-full">
                            <div className="stat py-3">
                                <div className="stat-title">Saldo</div>
                                <div className={`stat-value text-2xl ${balance.totalCredits <= 0 ? 'text-error' : ''}`}>
                                    {number.format(Math.floor(balance.totalCredits))}
                                </div>
                                <div className="stat-desc">minutos disponíveis</div>
                            </div>
                            <div className="stat py-3">
                                <div className="stat-title">Do plano neste mês</div>
                                <div className="stat-value text-2xl">
                                    {number.format(Math.floor(balance.plan?.remainingCredits ?? 0))}
                                </div>
                                <div className="stat-desc">de {number.format(balance.planCredits)} inclusos</div>
                            </div>
                            <div className="stat py-3">
                                <div className="stat-title">Usados no mês</div>
                                <div className="stat-value text-2xl">{number.format(Math.ceil(balance.monthUsedCredits))}</div>
                                {balance.monthUncoveredCredits > 0 && (
                                    <div className="stat-desc text-warning">
                                        {number.format(Math.ceil(balance.monthUncoveredCredits))} além do saldo
                                    </div>
                                )}
                            </div>
                        </div>
                        {balance.totalCredits <= 0 && (
                            <div className="alert alert-warning py-2">
                                <span className="text-sm">
                                    Os créditos acabaram. Reuniões em andamento continuam até o fim, mas novas salas
                                    só abrem depois da recarga.
                                </span>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <div className="flex flex-wrap items-end gap-4">
                <div className="form-control">
                    <label className="label pb-1.5" htmlFor="video-usage-from">
                        <span className="flex w-full items-baseline justify-between gap-3">
                            <span className="label-text font-medium">De</span>
                        </span>
                    </label>
                    <input
                        id="video-usage-from"
                        type="date"
                        className="input input-bordered input-sm"
                        value={from}
                        max={to}
                        onChange={event => setFrom(event.target.value)}
                    />
                </div>
                <div className="form-control">
                    <label className="label pb-1.5" htmlFor="video-usage-to">
                        <span className="flex w-full items-baseline justify-between gap-3">
                            <span className="label-text font-medium">Até</span>
                        </span>
                    </label>
                    <input
                        id="video-usage-to"
                        type="date"
                        className="input input-bordered input-sm"
                        value={to}
                        min={from}
                        onChange={event => setTo(event.target.value)}
                    />
                </div>
            </div>

            <div className="stats stats-vertical sm:stats-horizontal shadow-sm border border-base-200 w-full">
                {KINDS.map(kind => (
                    <div key={kind} className="stat py-3">
                        <div className="stat-title">{KIND_LABELS[kind]}</div>
                        <div className="stat-value text-2xl">{number.format(totals[kind])} min</div>
                    </div>
                ))}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : !byDay.length ? (
                <div className="text-center py-16">
                    <MdBarChart className="text-6xl mx-auto text-base-content/20 mb-3" />
                    <p className="font-medium">Nenhum consumo no período</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Dia</th>
                                {KINDS.map(kind => (
                                    <th key={kind} className="text-right">
                                        {KIND_LABELS[kind]}
                                    </th>
                                ))}
                                {balance?.enabled && <th className="text-right">Créditos</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {byDay.map(([day, entry]) => (
                                <tr key={day} className="hover">
                                    <td>{formatDay(day)}</td>
                                    {KINDS.map(kind => (
                                        <td key={kind} className="text-right text-base-content/70">
                                            {entry[kind]
                                                ? `${number.format(entry[kind]!.minutes)} min · ${entry[kind]!.participants} ${entry[kind]!.participants === 1 ? 'entrada' : 'entradas'}`
                                                : '-'}
                                        </td>
                                    ))}
                                    {balance?.enabled && (
                                        <td className="text-right font-medium">
                                            {number.format(Math.ceil(creditsByDay.get(day) ?? 0))}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
